import React, { useState } from 'react';
import { Box, Divider, Typography, Stack, useMediaQuery } from '@mui/material';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import { Link as RouterLink } from 'react-router-dom';
import defaultArticleThumbnail from '../../assets/default_article_thumbnail.svg';
import CommentCount from '../common/CommentCount';
import BookmarkCount from '../common/BookmarkCount';
import theme from '../../theme';
import CardTitle from '../common/CardTitle';

const NoticeCard = ({
  notice,
  imageMaxWidth = 220,
  actionButton,
  statsOnNewLine = false,
  referrerType,
}) => {
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const [imageError, setImageError] = useState(false);
  const noticeUrl = `/notice/${notice.slug || notice.id}`;

  const handleLinkToNotice = (e) => {
    if (!e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      navigateWithScrollMemory(noticeUrl, referrerType ? { state: { referrerType } } : undefined);
    }
  };

  const handleError = () => {
    setImageError(true);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        gap: { xs: 1.25, md: 2, lg: 2.5 },
      }}
    >
      {/* 이미지 */}
      <Box
        component={RouterLink}
        to={noticeUrl}
        state={referrerType ? { referrerType } : undefined}
        onClick={handleLinkToNotice}
        sx={{
          display: 'block',
          width: { xs: '30%', md: '20%', lg: '25%' },
          minWidth: { xs: 96, md: 168 },
          maxWidth: { md: imageMaxWidth },
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={imageError || !notice.imageUrl ? defaultArticleThumbnail : notice.imageUrl}
          alt={imageError || !notice.imageUrl ? 'Default article thumbnail' : notice.title}
          onError={handleError}
          sx={{
            width: '100%',
            aspectRatio: '280 / 188',
            cursor: 'pointer',
            objectFit: 'cover',
            display: 'block',
            border: `1px solid ${theme.palette.grey.line}`,
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <CardTitle title={notice.title} to={noticeUrl} onClick={handleLinkToNotice} />

        {/* 하단 정보 + actionButton */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
          }}
        >
          <Box>
            {/* 날짜 + 댓글/북마크 */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: { xs: 1.5, lg: 2 } }}>
              <Typography
                fontSize={{ xs: 12, md: 13, lg: 14 }}
                color={theme.palette.black[4]}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {notice.createdAt}
              </Typography>
              {isXs && !statsOnNewLine && (
                <>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      height: 8,
                      alignSelf: 'center',
                      backgroundColor: theme.palette.grey.line,
                    }}
                  />
                  <CommentCount commentCount={notice.commentCount} />
                  <BookmarkCount bookmarkCount={notice.bookmarkCount} />
                </>
              )}
            </Stack>

            {/* 댓글, 북마크 (별도 줄) */}
            {(!isXs || statsOnNewLine) && (
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ mt: { xs: 1.5, md: 1.5, lg: 2 } }}
              >
                <CommentCount commentCount={notice.commentCount} />
                <BookmarkCount bookmarkCount={notice.bookmarkCount} />
              </Stack>
            )}
          </Box>

          {/* 우측: actionButton */}
          {actionButton}
        </Box>
      </Box>
    </Box>
  );
};

export default NoticeCard;
